// Клиент для `/api/public/tool` — каталог инструмента публичного юзера.
// id — Uuid (string), payload — Record<string, unknown> (externally-tagged
// enum `tool_library::Tool`); распаковка/упаковка в tool_kinds.ts.
//
// ⚠ НЕ мигрировано на @webtypes (намеренно — задокументированный остаток drift):
//   1) ToolKind: локальный союз ТЕПЕРЬ в синке с генерёным (17 вариантов,
//      вкл. dv_collar/packer; см. ../tool_kind + tool_kinds.ts). Держим тип
//      локальным только чтобы `kind` нёс метадата-несущий локальный союз —
//      структурно он идентичен генерёному.
//   2) origin: серверные origin_state_uuid / origin_well_uuid — это
//      ПРОВЕНАНС (откуда инструмент импортирован), машинное поле, его не
//      «выставляют руками». В форме показываем read-only, на запись НЕ шлём
//      (на update `None` = «не трогать» ⇒ провенанс переживает round-trip).
//      tags — редактируются (см. ниже).
// Поэтому body-типы держим локально как СОВМЕСТИМОЕ подмножество: tags шлём,
// origin/clear_origin_* — нет (сервер применит дефолты / сохранит как было).

import type { ToolKind } from '../tool_kind'

export type ToolDto = {
  id: string
  kind: ToolKind
  title: string
  schema_version: number
  payload: Record<string, unknown>
  drilling_company_id: string | null
  // Провенанс (read-only в UI): откуда инструмент импортирован. null — создан
  // вручную / источник неизвестен. Сервер всегда присылает эти ключи.
  origin_state_uuid: string | null
  origin_well_uuid: string | null
  // Произвольные теги (нормализованы сервером: trim+lowercase, алфавит).
  tags: string[]
  created_at: string
  updated_at: string
}

export type CreateToolBody = {
  kind: ToolKind
  title: string
  payload: Record<string, unknown>
  drilling_company_id?: string | null
  // Теги: имена в любой форме, сервер нормализует и find-or-create'ит.
  // Опущено = пустой набор.
  tags?: string[]
}

// Семантика drilling_company:
//  - поле опущено или null → не трогать;
//  - поле = uuid → выставить;
//  - clear_drilling_company: true → отвязать (приоритетнее чем id).
// Двухуровневое различение «не трогать»/«обнулить» — через флаг, чтобы не
// тащить double-Option на бэке.
export type UpdateToolBody = {
  kind?: ToolKind | null
  title?: string | null
  payload?: Record<string, unknown> | null
  drilling_company_id?: string | null
  clear_drilling_company?: boolean
  // Теги — ПОЛНАЯ замена набора: массив = выставить ровно эти (пусто = очистить),
  // опущено (undefined) = не трогать. Origin не шлём (провенанс, см. шапку).
  tags?: string[]
}

const BASE = '/api/public/tool'

async function ensureOk(resp: Response): Promise<void> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}${text ? ': ' + text : ''}`)
  }
}

export async function listTools(): Promise<ToolDto[]> {
  const resp = await fetch(BASE, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

export async function createTool(body: CreateToolBody): Promise<ToolDto> {
  const resp = await fetch(BASE, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function updateTool(id: string, body: UpdateToolBody): Promise<ToolDto> {
  const resp = await fetch(`${BASE}/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

// 409 = деталь используется в компоновке (FK ON DELETE RESTRICT).
export class ToolInUseError extends Error {
  constructor() {
    super('Инструмент используется в компоновке — удалить нельзя')
  }
}

export async function deleteTool(id: string): Promise<void> {
  const resp = await fetch(`${BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  })
  if (resp.status === 409) throw new ToolInUseError()
  await ensureOk(resp)
}
